import { FieldType } from './constants';

///// IFrame outgoing event types (iframe -> parent window) /////

export enum IFrameEventName {
  Error = 'error',
  ReviewComplete = 'reviewComplete',
  DocumentSaved = 'documentSaved',
  DocumentSaveSummary = 'documentSavedSummary',
  DocumentRevisionRefresh = 'documentRevisionRefresh',
  FieldClicked = 'fieldClicked',
  FieldPlaced = 'fieldPlaced',
  FieldRemoved = 'fieldRemoved',
  ModalOpened = 'modalOpened',
  ModalClosed = 'modalClosed',
  HasChanges = 'hasChanges',
  HasNoChanges = 'hasNoChanges',
  MlDataRequest = 'mlDataRequest',
  SubmitFeedbackDataRequest = 'submitFeedbackDataRequest',
  UserActivity = 'userActivity',
  TokenExpired = 'tokenExpired',
  ProgressSaved = 'progressSaved',
  ActiveDocumentChanged = 'activeDocumentChanged',
  DownloadClicked = 'downloadClicked',
  ShareClicked = 'shareClicked',
  DocumentsLoaded = 'documentsLoaded',
}

export enum IFrameModalEventName {
  SignatureModal = 'signature',
}

export type IFrameHasChangesMeta = { canUndo: boolean; canRedo: boolean };
export type IFrameDocumentSavedMeta = {
  messageId?: string;
  documentId: string;
};
export type IFrameActiveDocumentChanged = {
  documentId: string;
};
export type IFrameDocumentSaveSummaryResult = {
  success: boolean;
  revision?: number;
};
export type IFrameDocumentSaveSummary = {
  [documentId: string]: boolean;
} & {
  documents?: {
    [documentId: string]: IFrameDocumentSaveSummaryResult;
  };
};
export type IFrameDocumentSaveSummaryMeta = {
  messageId?: string;
  success: boolean;
  summary: IFrameDocumentSaveSummary;
};
export type IFrameFieldMeta = {
  documentId: string;
  userId?: string;
  type: FieldType;
};
export type IFrameModalMeta = { name: IFrameModalEventName };
export type IFrameDownloadMeta = { fileUrl: string };
export type IFrameShareMeta = { fileUrl: string };
export type IFrameDocumentMeta = {
  revision?: number;
  name?: string;
};
export type IFrameDocumentsLoaded = {
  [documentId: string]: IFrameDocumentMeta;
};
export enum IFrameErrorTypes {
  MissingToken = 'missingToken',
  InvalidToken = 'invalidToken',
  ExpiredToken = 'expiredToken',
  FetchError = 'fetchError',
  SaveError = 'saveError',
  PageImageError = 'pageImageError',
  Unknown = 'unknownError',
}

export type IFrameErrorMeta = {
  error: IFrameErrorTypes;
  stack?: string;
};

export type IFrameEventMeta<T extends IFrameEventName> =
  T extends IFrameEventName.Error
    ? IFrameErrorMeta
    : T extends IFrameEventName.DocumentSaved
    ? IFrameDocumentSavedMeta
    : T extends IFrameEventName.ActiveDocumentChanged
    ? IFrameActiveDocumentChanged
    : T extends IFrameEventName.DocumentSaveSummary
    ? IFrameDocumentSaveSummaryMeta
    : T extends IFrameEventName.HasChanges | IFrameEventName.HasNoChanges
    ? IFrameHasChangesMeta
    : T extends
        | IFrameEventName.FieldClicked
        | IFrameEventName.FieldPlaced
        | IFrameEventName.FieldRemoved
    ? IFrameFieldMeta
    : T extends IFrameEventName.ModalOpened | IFrameEventName.ModalClosed
    ? IFrameModalMeta
    : T extends IFrameEventName.DownloadClicked
    ? IFrameDownloadMeta
    : T extends IFrameEventName.ShareClicked
    ? IFrameShareMeta
    : T extends IFrameEventName.DocumentsLoaded
    ? IFrameDocumentsLoaded
    : undefined;

export type IFrameEventNameWithMeta<T extends IFrameEventName> =
  IFrameEventMeta<T> extends undefined ? never : T;
export type IFrameEventNameWithoutMeta<T extends IFrameEventName> =
  IFrameEventMeta<T> extends undefined ? T : never;

export type IFrameEvent<T extends IFrameEventName> = {
  event: T;
  meta?: IFrameEventMeta<T>;
};

///// IFrame incoming message types (parent window -> iframe) /////

export enum IFrameMessageName {
  SaveDocument = 'saveDocument',
  UndoChanges = 'undoDocumentChanges',
  RedoChanges = 'redoDocumentChanges',
  SaveProgress = 'saveProgress',
  EditorVersion = 'editorVersion',
}

export type IFrameSaveDocumentMessageMeta = {
  messageId?: string;
};
export type IFrameEditorVersionMeta = {
  version: string;
};

export type IFrameMessageMeta<T extends IFrameMessageName> =
  T extends IFrameMessageName.SaveDocument
    ? IFrameSaveDocumentMessageMeta
    : T extends IFrameMessageName.EditorVersion
    ? IFrameEditorVersionMeta
    : undefined;

export type IFrameMessage<T extends IFrameMessageName> = {
  event: T;
  meta?: IFrameMessageMeta<T>;
};
